import React, { useEffect, useRef, useCallback } from 'react';
import { FaImage, FaVideo, FaCalendarAlt, FaSpinner } from 'react-icons/fa';
import { useLazyGetPostsQuery } from '../store/api/postApi';
import { useGetProfileQuery } from '../store/api/profileApi';
import { useAuth } from '../hooks/useAuth';
import CreatePostModal from './createPost';
import Post from './Post';
import styles from './Feed.module.css';

export default function Feed() {
  const { data: user } = useGetProfileQuery();
  const { userId: loggedInUserId } = useAuth();

  const [
    fetchPosts,
    { data, isLoading, isFetching, isError }
  ] = useLazyGetPostsQuery();

  const observerRef = useRef(null);
  const isFetchingRef = useRef(false);

  const posts = data?.items ?? [];
  const nextCursor = data?.nextCursor ?? null;
  const hasMore = Boolean(nextCursor);

  const [isModalOpen, setIsModalOpen] = React.useState(false);

  // Initial load
  useEffect(() => {
    fetchPosts({ pageSize: 20 });
  }, [fetchPosts]);

  // Infinite scroll observer
  useEffect(() => {
    if (!observerRef.current || !hasMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          hasMore &&
          !isFetchingRef.current
        ) {
          isFetchingRef.current = true;

          fetchPosts({
            cursor: nextCursor,
            pageSize: 20,
          });
        }
      },
      { rootMargin: '150px' }
    );

    observer.observe(observerRef.current);

    return () => observer.disconnect();
  }, [fetchPosts, hasMore, nextCursor]);

  // Reset fetch lock after request finishes
  useEffect(() => {
    if (!isFetching) {
      isFetchingRef.current = false;
    }
  }, [isFetching]);

  const handlePostCreated = useCallback(() => {
    fetchPosts({ pageSize: 20 });
  }, [fetchPosts]);

  const currentUser = {
    name: user?.name || 'User',
    photo: user?.profilePhotoUrl,
  };

  const initials = currentUser.name
    ?.split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className={styles.feed}>
      {/* Create Post */}
      <div className={styles.createPostCard}>
        <div className={styles.createPostHeader}>
          {currentUser.photo ? (
            <img src={currentUser.photo} alt={currentUser.name} className={styles.userPhoto} />
          ) : (
            <div className={styles.noPhoto}>{initials || '?'}</div>
          )}
          <button
            onClick={() => setIsModalOpen(true)}
            className={styles.startPostBtn}
          >
            Start a post
          </button>
        </div>

        <div className={styles.createPostActions}>
          <button className={styles.actionBtn} onClick={() => setIsModalOpen(true)}>
            <FaImage /> Photo / Video
          </button>
          {/* <button className={styles.actionBtn} onClick={() => setIsModalOpen(true)}>
            <FaVideo /> Video
          </button> */}
          {/* <button className={styles.actionBtn} onClick={() => setIsModalOpen(true)}>
            <FaCalendarAlt /> Event
          </button> */}
        </div>
      </div>

      {/* Initial loading */}
      {isLoading && (
        <div className={styles.loadingContainer}>
          <FaSpinner className={styles.spinner} />
          <p>Loading posts...</p>
        </div>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <div className={styles.errorContainer}>
          <p>Failed to load posts.</p>
          <button onClick={() => fetchPosts({ pageSize: 20 })}>
            Retry
          </button>
        </div>
      )}

      {/* Empty */}
      {!isLoading && posts.length === 0 && (
        <div className={styles.emptyState}>
          <p>No posts yet.</p>
        </div>
      )}

      {/* Posts */}
      {posts.map((post) => {
        const isShared = post.type === "SharedPost";
        const user = isShared ? post.sharedBy : post.user;

        return (
          <Post
            key={post.id}
            post={post}
            isOwner={user?.id === loggedInUserId}
          />
        );
      })}

      {/* Infinite scroll sentinel */}
      {hasMore && (
        <div ref={observerRef} className={styles.observerTarget} />
      )}

      {/* Loading more */}
      {isFetching && posts.length > 0 && (
        <div className={styles.loadingContainer}>
          <FaSpinner className={styles.spinner} />
          <p>Loading more...</p>
        </div>
      )}

      {/* Modal */}
      <CreatePostModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userPhoto={currentUser.photo}
        userName={currentUser.name}
        onPostCreated={handlePostCreated}
      />
    </div>
  );
}
